<script>
    import { onMount, onDestroy } from 'svelte';
    import { datepickerRange, getDateRangeFromPreset, resetFlatpickrDate } from '@lib/datepicker/datepickerRange';
    import { datePresetOptions } from '@lib/models/datePresetOptions';
    import { perPageOptions } from '@lib/models/perPageOptions'
    import Pagination from '@components/Pagination.svelte';
    import ItemPerPageSelect from '@components/ItemPerPageSelect.svelte';
    import PopupInquiry from '@components/PopupInquiry.svelte';
    import { getInquiry } from '@lib/api/inquiry';
    import { formatDateToKorean } from '@lib/util/dateUtils';

    let isInitialMount = true;          // 초기 mount 여부 확인
    let isCategoryOpen = false;         // 검색 조건 카테고리 선택 드롭다운 열림 여부
    let isDatePresetOpen = false;       // 날짜 프리셋 선택 드롭다운 열림 여부
    let isItemsPerPageOpen = false;     // 테이블 조회 개수 선택 드롭다운 열림 여부
    let isRegisterPopupOpen = false;    // 1:1문의 등록 팝업 열림 여부
    let isModifyPopupOpen = false;      // 1:1문의 수정 팝업 열림 여부

    let viewCategory = '';              // 사용자에게 표시할 카테고리명 (ex. 상태, 제목 등)
    let viewDataPreset = '';            // 사용자에게 표시할 날짜 프리셋명 (ex. 오늘, 어제 등)
    let viewItemsPerPage = '';          // 사용자에게 표시할 테이블 조회 개수

    let status = 'all';                 // 초기 선택값
    let selectedInquiry                 // 선택된 1:1문의

    let categoryDropdownEl = null;      // 카테고리 드롭다운 DOM 요소 참조
    let datePresetDropdownEl = null;    // 날짜 프리셋 드롭다운 DOM 요소 참조
    let viewCountDropdownEl = null;     // 테이블 조회 개수 드롭다운 DOM 요소 참조

    let inquiries = [];                 // API에서 받아온 1:1문의 목록
    let loading = true;                 // 데이터 로딩 상태
    let size = 20;                      // 페이지당 공지사항 수 (기본: 20)
    let total = 0;                      // 전체 개수

    let error = '';                     // 에러 메시지 (빈 문자열이면 에러 없음)

    let totalPages = 0; 
    let currentPage = 1;

    let startRef = {                    // 시작 날짜 input에 바인딩된 flatpickr 인스턴스 조작용 객체
        onStartChange: (dateStr) => {
            searchParam.startDate = dateStr;
        }
    };

    let endRef = {                      // 종료 날짜 input에 바인딩된 flatpickr 인스턴스 조작용 객체
        onEndChange: (dateStr) => {
            searchParam.endDate = dateStr;
        }
    };

    const filterOptions = [
        { label: '문의유형', value: 'category' },
        { label: '제목', value: 'title' },
        { label: '사용자 ID', value: 'created_id' },
        { label: '사용자', value: 'created_name' },
        { label: '관리자', value: 'updated_name' },
    ];

    const defaultSearchParam = {
        category: "",
        searchKeyword: "",
        startDate: "",
        endDate: "",
        status: "",
        page: 1,
        size: size
    };

    const statusOptions = [
        { id: 'radio1', label: '전체', value: 'all', checked: true },
        { id: 'radio2', label: '접수중', value: '접수중', checked: false },
        { id: 'radio3', label: '답변완료', value: '답변완료', checked: false },
        { id: 'radio3', label: '문의취소', value: '문의취소', checked: false }
    ];

    let searchParam = { ...defaultSearchParam }; // 초기값 세팅

    $: searchParam.status = status;

    /**
     * 1:1문의 목록을 API로부터 불러와 `inquiries` 배열에 저장합니다.
     *
     * @async
     * @function fetchInquiries
     * @param {Object} searchParam - 검색 조건 객체
     * @returns {Promise<void>}
     */
    async function fetchInquiries(searchParam) {
        console.log(searchParam);
        loading = true;
        try {
            const data = await getInquiry(searchParam);
            total = data.total;
            inquiries = data.items.map((/** @type {any} */ item) => ({ ...item, checked: false }));

            totalPages = Math.ceil(total / searchParam.size);
            
            // 첫 번째 플랜 자동 선택
            if (inquiries.length > 0) {
                selectedInquiry = inquiries[0];
            } else {
                selectedInquiry = null;
            }
        } catch (err) {
            console.error('플랜 불러오기 실패:', err);
            error = '플랜 정보를 불러오지 못했습니다.';
        } finally {
            loading = false;
        }
    }

    // 검색 조건 카테고리 드롭다운 열기/닫기 토글
    function toggleCategoryDropdown() {
        isCategoryOpen = !isCategoryOpen;
    }

    // 날짜 프리셋(오늘/이번주 등) 드롭다운 열기/닫기 토글
    function toggleDataPresetDropdown() {
        isDatePresetOpen = !isDatePresetOpen;
    }

    function toggleItemsPerPageDropdown() {
        isItemsPerPageOpen = !isItemsPerPageOpen;
    }

    /**
     * 1:1문의 등록 팝업을 엽니다.
     * 사용자 UI 상에서 "1:1문의 등록" 버튼 클릭 시 호출됩니다.
     */
    function openRegisterPopup() {
        isRegisterPopupOpen = true;
    }

    /**
     * 1:1문의 등록 팝업을 닫습니다.
     * 팝업 내 '닫기' 버튼 또는 외부 클릭 등으로 트리거됩니다.
     */
    function closeRegisterPopup() {
        isRegisterPopupOpen = false;
    }

    /**
     * 1:1문의 수정 팝업을 엽니다.
     * 예: "수정" 버튼 클릭 시 해당 1:1문의 데이터를 불러온 후 호출됩니다.
     */
    function openModifyPopup() {
        isModifyPopupOpen = true;
    }

    /**
     * 1:1문의 수정 팝업을 닫습니다.
     * 팝업 내 '닫기' 버튼 또는 외부 클릭 등으로 트리거됩니다.
     */
    function closeModifyPopup() {
        isModifyPopupOpen = false;
    }  
    /**
     * 카테고리 선택 시 호출됨
     * @param {{ label: any; value: any; }} filterOptions
     */
    function selectCategory(filterOptions) {
        viewCategory = filterOptions.label;
        searchParam.category = filterOptions.value;
        isCategoryOpen = false; // 선택 후 드롭다운 닫기
    }

    /**
     * 드롭다운 외부 클릭 시 드롭다운을 닫기 위한 이벤트 핸들러
     * @param {{ target: any; }} event 클릭 이벤트 객체
     */
    function handleClickOutside(event) {
        // 통화 드롭다운 외부 클릭 감지
        if (isCategoryOpen && categoryDropdownEl && !categoryDropdownEl.contains(event.target)) {
            isCategoryOpen = false;
        }

        if (isDatePresetOpen && datePresetDropdownEl && !datePresetDropdownEl.contains(event.target)) {
            isDatePresetOpen = false;
        }        
    }

     /**
     * 날짜 프리셋(오늘, 어제, 이번 주 등)을 선택했을 때 실행되는 핸들러
     *
     * 1. 프리셋에 맞는 날짜 범위 계산
     * 2. UI 및 내부 상태 업데이트 (viewDataPreset, searchParam 등)
     * 3. flatpickr 인스턴스를 통해 실제 input 요소의 날짜 갱신
     *
     * @param {{ label: string, value: string }} datePresetOptions - 선택된 날짜 프리셋 옵션 객체
     */
    async function selectDatePreset(datePresetOptions) {
        const result = getDateRangeFromPreset(datePresetOptions.value);
       
        if (!result) return;


        viewDataPreset = datePresetOptions.label;
        searchParam.startDate = result.startDate;
        searchParam.endDate = result.endDate;
        isDatePresetOpen = false;


        // Date 객체로 변환
        const start = new Date(result.startDate);
        const end = new Date(result.endDate);

        resetFlatpickrDate(startRef.instance, start);
        resetFlatpickrDate(endRef.instance, end);

    }

    function searchBoxReset(){
        // searchParam 초기화
        searchParam = { ...defaultSearchParam };

        // UI 관련 리셋 로직 실행
        selectCategory(filterOptions[0]);
        selectDatePreset(datePresetOptions[0]);

        status = 'all';

    }
    function search(){
        fetchInquiries(searchParam);
    }

    /**
     * @param {number} page
     */
    function handlePageChange(page) {
        currentPage = page;
        searchParam.page = page;
        fetchInquiries(searchParam);
    }

    /**
     * @param {object} viewCountObject
     */
    function selectItemsPerPage(viewCountObject) {
        viewItemsPerPage = viewCountObject.label;
        size = viewCountObject.value;
        searchParam.size = viewCountObject.value;
        searchParam.page = 1;
        isItemsPerPageOpen = false;

        // 초기 mount일 때는 호출하지 않음
        if (!isInitialMount) {
            fetchInquiries(searchParam);
        }
    }

    function getStatusClass(status) {
        if (status === '접수중') return 'btn btn-processing';
        if (status === '답변완료') return 'btn btn-complete';
        if (status === '문의취소') return 'btn btn-cancel';
        return 'btn';
    }

    function handleSelect(option) {
        selectItemsPerPage(option);
    }

    function selectInquiry(inquiry) {
        selectedInquiry = inquiry;

        if(selectedInquiry.updated_name == null && selectedInquiry.updated_at == null ){
            openRegisterPopup();
        }else{
            openModifyPopup()
        }
    }

    /**
     * 1:1문의 팝업에서 저장 후 닫기 동작을 처리합니다.
     */
    function handleSave() {
        fetchInquiries(searchParam);
        isRegisterPopupOpen = false;
    }

    /**
     * 1:1문의 수정 팝업에서 저장 후 닫기 동작을 처리합니다.
     */
    function handleModify() {
        fetchInquiries(searchParam);
        isModifyPopupOpen = false;
    }
    onMount(async () => {
        document.addEventListener('click', handleClickOutside);
        selectCategory(filterOptions[0]);
        selectDatePreset(datePresetOptions[0]);
        selectItemsPerPage(perPageOptions[0]);

        fetchInquiries(searchParam);

        isInitialMount = false; // 초기호출 수행 시 false로 반환
    });

    onDestroy(() => {
        document.removeEventListener('click', handleClickOutside);
    });
</script>
<div class="inquiry-wrap d-flex flex-column h-100">
    <p class="font-20 font-weight-700">1:1 문의</p>
    <section class="box-gray d-flex align-center mt-5">
        <article class="form-block">
            <form action="" class="d-flex align-center flex-wrap gap-3">
                <fieldset class="width-160">
                    <legend>사용자 관리 input 셀렉트</legend>
                    <div class="custom-select w-100" class:active={isCategoryOpen} bind:this={categoryDropdownEl}>
                        <button type="button" class="custom-select__selected" on:click={toggleCategoryDropdown}>
                            <span>{viewCategory}</span>
                            <i class="icon icon-dropdown"></i>
                        </button>
                        <ul class="custom-select__options">
                            {#each filterOptions as option}
                            <li class="custom-select__option">
                                <button
                                    type="button"
                                    class="custom-select__button"
                                    on:click={() => selectCategory(option)}
                                    >
                                    {option.label}
                                </button>
                            </li>
                            {/each}
                        </ul>
                    </div>
                </fieldset>
                <fieldset class="width-200">
                    <legend>사용자 관리 input 텍스트</legend>
                    <label for="input-field-userMng">
                        <input type="text" name="" id="input-field-userMng" class="input-field" title="userMng input 텍스트" placeholder="" bind:value={searchParam.searchKeyword}>
                    </label>
                </fieldset>

            </form>
        </article>
        <article class="form-block ml-12">
            <p class="form-block__title">기간 검색</p>
            <form action="" class="d-flex align-center ml-5 gap-3">
                <fieldset>
                    <legend>input style default input 달력</legend>
                    <div class="d-flex align-items-center flex-row js-datepicker-range" style="height: 40px;">
                    <div class="ui-form-block h-100">
                        <input
                        type="text"
                        class="input-field h-100 js-datepicker-range__min"
                        title="시작 날짜"
                        use:datepickerRange={{ type: 'start', ref: startRef }}
                        />
                    </div>
                    <span class="el-hyphen d-flex align-center mx-3">~</span>
                    <div class="ui-form-block h-100">
                        <input
                        type="text"
                        class="input-field h-100 js-datepicker-range__max"
                        title="종료 날짜"
                        use:datepickerRange={{ type: 'end', ref: endRef }}
                        />
                    </div>
                    </div>

                </fieldset>
                <fieldset class="width-80">
                    <legend>요일 input 셀렉트</legend>
                    <div class="custom-select w-100" class:active={isDatePresetOpen} bind:this={datePresetDropdownEl}>
                         <button type="button" class="custom-select__selected" on:click={toggleDataPresetDropdown}>
                            <span>{viewDataPreset}</span>
                            <i class="icon icon-dropdown"></i>
                        </button>
                        <ul class="custom-select__options">
                            {#each datePresetOptions as option}
                            <li class="custom-select__option">
                                <button
                                    type="button"
                                    class="custom-select__button"
                                    on:click={() => selectDatePreset(option)}
                                    >
                                    {option.label}
                                </button>
                            </li>
                            {/each}
                        </ul>
                    </div>
                </fieldset>
            </form>
        </article>
        <article class="form-block ml-12">
            <p class="form-block__title">상태</p>
           <form action="" class="d-flex align-center flex-wrap gap-3 ml-5">
                {#each statusOptions as option, index}
                <fieldset class={index > 0 ? 'ml-5' : ''}>
                    <legend>1:1문의 상태 input 라디오</legend>
                    <div class="custom-radio inline-text">
                    <input
                        type="radio"
                        name="radio-inquiry"
                        id={"input-field-adminRadio" + (index + 1)}
                        bind:group={status}
                        value={option.value}
                        checked={option.checked}
                        title="default input 라디오"
                    />
                    <label for={"input-field-adminRadio" + (index + 1)}>
                        {option.label}
                    </label>
                    </div>
                </fieldset>
                {/each}
            </form>
        </article>
        <article class="form-block ml-auto">
            <button type="button" class="btn btn-reset" aria-label="reset" on:click={searchBoxReset}>
                <i class="icon icon-reset"></i>
            </button>
            <button type="button" class="btn bg-darkgray height-40 ml-1" on:click={search}>조회</button>
        </article>
    </section>
    <div class="d-flex align-center mt-5">
        <p class="table-count">
            총 <span>{total}</span> 건
        </p>
        <form action="" class="d-flex align-center flex-wrap gap-3 ml-auto">
            <fieldset class="width-120">
                <legend>사용자 관리 테이블 개수 input 셀렉트</legend>
                <div class="custom-select w-100" class:active={isItemsPerPageOpen} bind:this={viewCountDropdownEl}>
                    <button type="button" class="custom-select__selected" on:click={toggleItemsPerPageDropdown}>
                        <span>{viewItemsPerPage}</span>
                        <i class="icon icon-dropdown"></i>
                    </button>
                    <ItemPerPageSelect options={perPageOptions} onSelect={handleSelect} />
                </div>
            </fieldset>
        </form>
    </div>
    <div class="d-flex flex-column h-100 mt-2 bg-white box-border">
        <div class="table-wrap">
            <table class="table-basic table-basic-inquiry">
                <caption></caption>
                <colgroup>
                    <col style="width:5%">
                    <col style="width:10%">
                    <col style="width:10%">
                    <col style="width:auto">
                    <col style="width:10%">
                    <col style="width:10%">
                    <col style="width:10%">
                    <col style="width:10%">
                    <col style="width:10%">
                </colgroup>
                <thead>
                <tr>
                    <th scope="col">번호</th>
                    <th scope="col">상태</th>
                    <th scope="col">문의유형</th>
                    <th scope="col">제목</th>
                    <th scope="col">사용자 ID</th>
                    <th scope="col">사용자 </th>
                    <th scope="col">등록일시</th>
                    <th scope="col">관리자</th>
                    <th scope="col">답변일시</th>
                </tr>
                </thead>
                <tbody>
                {#if loading}
                    <tr>
                    <td colspan="6" class="text-center">로딩 중...</td>
                    </tr>
                {:else if error}
                    <tr>
                    <td colspan="6" class="text-center text-red">{error}</td>
                    </tr>
                {:else if inquiries.length === 0}
                    <tr>
                    <td colspan="6" class="text-center">등록된 플랜이 없습니다.</td>
                    </tr>
                {:else}
                    {#each inquiries as inquiry, index}
                    <tr>
                        <td>{index + 1}</td>
                        <td>
                        <button type="button" class={getStatusClass(inquiry.status)}>
                            {inquiry.status}
                        </button>
                        </td>
                        <td>{inquiry.category}</td>
                        <td>
                        <button type="button" on:click={() => selectInquiry(inquiry)}>
                            {inquiry.title}
                        </button>
                        </td>
                        <td>{inquiry.created_id}</td>
                        <td>{inquiry.created_name}</td>
                        <td>{formatDateToKorean(inquiry.created_at)}</td>
                        <td>{inquiry.updated_name}</td>
                        <td>{formatDateToKorean(inquiry.updated_at)}</td>
                    </tr>
                    {/each}
                {/if}
            </tbody>
            </table>
        </div>
    </div>
    <Pagination {currentPage} {totalPages}  pageChange={handlePageChange}></Pagination>

    <!-- <div class="btn-wrap justify-center mt-2">
        <button type="button" class="btn border-blue">일괄 다운로드</button>
    </div> -->
</div>

{#if isRegisterPopupOpen}
  <PopupInquiry mode="create"  initialData={selectedInquiry} onClose={closeRegisterPopup} onSave={handleSave} />
{/if}

{#if isModifyPopupOpen}
  <PopupInquiry mode="modify" initialData={selectedInquiry} onClose={closeModifyPopup} onSave={handleModify} />
{/if}